// /regalo — tarjetas regalo (Stripe): importe libre, una clase, un bono o un mes de
// membresía, con ocasión, nota personal, frases sugeridas y vista previa de la tarjeta.
import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Gift, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { crearCheckoutRegalo, getBonosTipos, ApiError, API_BASE } from '../lib/api';
import { useAuth, readToken } from '../context/AuthContext';
import { formatEur } from '../components/CodigoDescuento';
import EmpresasCTA from '../components/EmpresasCTA';
import { OCASIONES, ORDEN_OCASIONES, FRASES, TXT } from '../lib/regaloDatos';

const IMPORTES = [25, 50, 75, 100];

export default function Regalo() {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const X = TXT[lang] || TXT.es;
  const [params] = useSearchParams();
  const stripe = params.get('stripe');
  const [importe, setImporte] = useState(50);
  const [otro, setOtro] = useState('');
  const [producto, setProducto] = useState(null); // null = importe libre; si no, bono_tipo
  const [productos, setProductos] = useState([]);
  const [ocasion, setOcasion] = useState('especial');
  const [lema, setLema] = useState('');
  const [form, setForm] = useState({ comprador_nombre: (user && user.nombre) || '', comprador_email: '', destinatario_nombre: '', destinatario_email: '', mensaje: '' });
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const importeLibre = otro ? Number(String(otro).replace(',', '.')) : importe;
  const importeFinal = producto ? Number(producto.precio_euros) : importeLibre;

  useEffect(() => {
    if (user && user.nombre) setForm((f) => (f.comprador_nombre ? f : { ...f, comprador_nombre: user.nombre }));
  }, [user]);
  useEffect(() => {
    let vivo = true;
    getBonosTipos(lang).then((r) => {
      if (vivo) setProductos((r.data || []).filter((b) => Number(b.precio_euros) > 0));
    }).catch(() => {});
    fetch(`${API_BASE}/api/portada?idioma=${lang}`).then((r) => r.json())
      .then((r) => { if (vivo) setLema((r.contenido || {})['regalo.lema'] || ''); }).catch(() => {});
    return () => { vivo = false; };
  }, [lang]);

  const oc = OCASIONES[ocasion];
  const frases = (FRASES[ocasion] || {})[lang] || FRASES[ocasion].es;
  const clase = productos.find((b) => b.tipo === 'clase_suelta');
  const bonos = productos.filter((b) => b.tipo !== 'clase_suelta');

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!producto && !(importeFinal >= 10 && importeFinal <= 500)) { setError(t('regalo.errores.importe')); return; }
    setEnviando(true);
    try {
      const token = isAuthenticated ? readToken() : null;
      const r = await crearCheckoutRegalo({ ...form, importe_euros: importeFinal, idioma: lang, ocasion,
        producto_bono_tipo_id: producto ? producto.id : undefined,
        comprador_email: isAuthenticated ? user?.email : form.comprador_email }, token);
      if (r?.url) { window.location.href = r.url; return; }
      throw new Error('sin url');
    } catch (err) {
      setEnviando(false);
      setError(err instanceof ApiError && err.code ? t('regalo.errores.' + err.code, { defaultValue: err.message }) : t('regalo.errores.generico'));
    }
  };

  const nombreProd = (b) => `${b.nombre} · ${formatEur(Number(b.precio_euros), lang)}`;

  return (
    <main className="clase-page regalo-page">
      <div className="clase-page-inner">
        <div className="clase-page-head">
          <div className="clase-page-avatar"><Gift size={32} strokeWidth={1.25} /></div>
          <div className="clase-page-head-info">
            <h1 className="clase-page-title">{lema || t('regalo.lema')}</h1>
            <p className="clase-page-resumen">{t('regalo.sub')}</p>
          </div>
        </div>

        {stripe === 'success' && (
          <div className="evento-banner evento-banner--ok"><CheckCircle2 size={18} /> <span>{t('regalo.exito')}</span></div>
        )}
        {stripe === 'cancelled' && (
          <div className="evento-banner evento-banner--warn"><AlertCircle size={18} /> <span>{t('regalo.cancelado')}</span></div>
        )}

        <form className="modal-form regalo-form" onSubmit={submit}>
          <div className="modal-campo">
            <label>{X.producto}</label>
            <div className="regalo-importes">
              <button type="button" className={'regalo-chip' + (!producto ? ' activo' : '')} onClick={() => setProducto(null)}>{X.importe_libre}</button>
              {clase && (
                <button type="button" className={'regalo-chip' + (producto && producto.id === clase.id ? ' activo' : '')} onClick={() => setProducto(clase)}>
                  {X.una_clase} · {formatEur(Number(clase.precio_euros), lang)}
                </button>
              )}
              {bonos.map((b) => (
                <button type="button" key={b.id} className={'regalo-chip' + (producto && producto.id === b.id ? ' activo' : '')} onClick={() => setProducto(b)}>
                  {nombreProd(b)}
                </button>
              ))}
            </div>
          </div>

          {!producto && (
            <div className="modal-campo">
              <label>{t('regalo.importe')}</label>
              <div className="regalo-importes">
                {IMPORTES.map((n) => (
                  <button type="button" key={n} className={'regalo-chip' + (!otro && importe === n ? ' activo' : '')}
                    onClick={() => { setImporte(n); setOtro(''); }}>{formatEur(n, lang)}</button>
                ))}
                <input type="number" min="10" max="500" step="1" placeholder={t('regalo.otro')} value={otro}
                  onChange={(e) => setOtro(e.target.value)} className="regalo-otro" />
              </div>
            </div>
          )}

          <div className="modal-campo">
            <label>{X.ocasion}</label>
            <div className="regalo-importes">
              {ORDEN_OCASIONES.map((id) => (
                <button type="button" key={id} className={'regalo-chip' + (ocasion === id ? ' activo' : '')} onClick={() => setOcasion(id)}>
                  {OCASIONES[id].titulo[lang] || OCASIONES[id].titulo.es}
                </button>
              ))}
            </div>
          </div>

          <div className="modal-campo"><label>{t('regalo.tu_nombre')}</label>
            <input type="text" required value={form.comprador_nombre} onChange={set('comprador_nombre')} maxLength={80} /></div>
          {!isAuthenticated && (
            <div className="modal-campo"><label>{t('regalo.tu_email')}</label>
              <input type="email" required value={form.comprador_email} onChange={set('comprador_email')} /></div>
          )}
          <div className="modal-campo"><label>{t('regalo.dest_nombre')}</label>
            <input type="text" required value={form.destinatario_nombre} onChange={set('destinatario_nombre')} maxLength={80} /></div>
          <div className="modal-campo"><label>{t('regalo.dest_email')}</label>
            <input type="email" required value={form.destinatario_email} onChange={set('destinatario_email')} />
            <small className="mw-ayuda">{t('regalo.dest_ayuda')}</small></div>
          <div className="modal-campo"><label>{t('regalo.mensaje')}</label>
            <textarea rows={3} maxLength={300} placeholder={X.mensaje_ph} value={form.mensaje} onChange={set('mensaje')} />
            <small className="mw-ayuda">{X.frases}</small>
            <div className="regalo-frases">
              {frases.map((f) => (
                <button type="button" key={f} className="regalo-frase" onClick={() => setForm((s) => ({ ...s, mensaje: f }))}>{f}</button>
              ))}
            </div>
          </div>

          <div className="modal-campo">
            <label>{X.vista}</label>
            <div className="regalo-vista" style={{ '--oc': oc.color }}>
              <div className="regalo-vista-cab">
                <img className="regalo-vista-logo" src="/logo-blanco.png" alt="I-DO-YOGA" />
                <strong>{oc.titulo[lang] || oc.titulo.es}</strong>
                {form.destinatario_nombre && <span>{X.para} {form.destinatario_nombre}</span>}
              </div>
              <div className="regalo-vista-cuerpo">
                <div className="regalo-vista-importe">{producto ? producto.nombre : formatEur(importeFinal || 0, lang)}</div>
                {form.mensaje && <p className="regalo-vista-msg">“{form.mensaje}”</p>}
                {(form.comprador_nombre || (isAuthenticated && user?.nombre)) && (
                  <small>— {X.de} {form.comprador_nombre || user?.nombre}</small>
                )}
                <div className="regalo-vista-codigo">IDY-XXXX-XXXX</div>
              </div>
            </div>
          </div>

          <p className="modal-aviso">{t('regalo.condiciones')}</p>
          {error && <div className="modal-error-general"><AlertCircle size={16} /><span>{error}</span></div>}
          <button type="submit" className="clase-page-cta" disabled={enviando}>
            {enviando ? <><Loader2 size={16} className="spin" /> {t('auth.enviando')}</> : t('regalo.comprar', { importe: formatEur(importeFinal || 0, lang) })}
          </button>
          <Link to="/horarios" className="clase-page-cta clase-page-cta--secondary">{t('regalo.ver_horarios')}</Link>
        </form>
        <EmpresasCTA compacto />
      </div>
    </main>
  );
}
